// src/AuthContext.js
// Auth context wired exclusively to the OWNERS table.
// The old CompanyUser / trivana_company_users table is no longer used for public auth.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import API_BASE_URL from "./config";

const AuthContext = createContext();

const STORAGE_KEY = "trivana_owner_auth";

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(() => readStorage());
  const [loading, setLoading] = useState(false);

  // Sync to localStorage whenever authData changes
  useEffect(() => {
    if (authData) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [authData]);

  // Derived user object — compatible with existing `user.email` usage in the codebase
  const user = authData
    ? {
        email: authData.email,
        ownerName: authData.ownerName,
        ownerId: authData.ownerId,
        contactPhone: authData.contactPhone,
        isVerified: authData.isVerified,
        phoneVerified: authData.phoneVerified,
        status: authData.status,
        onboardingStep: authData.onboardingStep,
        merchantSso: authData.merchantSso,
      }
    : null;

  /**
   * Login against the OWNERS table via /owners/login
   * Returns the full owner profile + onboardingStep.
   */
  const login = async (email, password) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/owners/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Login failed: ${res.status}`);
      }
      // data: { accessToken, email, ownerName, ownerId, isVerified, phoneVerified, status, onboardingStep }
      
      // Perform background login to the merchant portal auth for SSO
      try {
        const ssoRes = await fetch(`${API_BASE_URL}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        if (ssoRes.ok) {
          const ssoData = await ssoRes.json().catch(() => ({}));
          if (ssoData && ssoData.token) {
            data.merchantSso = {
              token: ssoData.token,
              email: ssoData.email,
              role: ssoData.role
            };
          }
        }
      } catch (ssoErr) {
        console.warn("Merchant Portal SSO login failed:", ssoErr);
      }

      setAuthData(data);
      return data;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register a new owner via /owners/register (onboarding step 0).
   * After registration the user is NOT yet verified — they go to step 1 (Verify).
   */
  const signup = async (ownerName, email, phone, password) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/owners/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerName, contactEmail: email, contactPhone: phone, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Registration failed: ${res.status}`);
      }
      // Store minimal auth data so subsequent calls know who the user is
      const partial = {
        accessToken: null,
        email: data.contactEmail || email,
        ownerName: data.ownerName || ownerName,
        ownerId: data.ownerId,
        contactPhone: data.contactPhone || phone,
        isVerified: false,
        phoneVerified: false,
        status: "PENDING",
        onboardingStep: 1, // next step: Verify
      };
      setAuthData(partial);
      return partial;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Google login — still uses CompanyUser table for website-level auth.
   * After Google login on the public site, we redirect to /onboarding
   * where the owner table link is established.
   */
  const googleLogin = async (idToken) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Google login failed: ${res.status}`);
      }
      // Try to find the matching owner record for this Google email
      const email = data.email;
      const ownerRes = await fetch(
        `${API_BASE_URL}/owners/onboarding-progress?email=${encodeURIComponent(email)}`
      ).catch(() => null);

      let ownerStep = 0;
      let ownerProfile = {};
      if (ownerRes && ownerRes.ok) {
        const ownerData = await ownerRes.json().catch(() => ({}));
        ownerStep = ownerData.currentStep ?? 0;
        ownerProfile = ownerData;
      }

      const merged = {
        accessToken: data.accessToken || data.access_token || null,
        email,
        ownerName: ownerProfile.ownerName || data.name || email,
        ownerId: ownerProfile.ownerId || null,
        isVerified: ownerProfile.isVerified || false,
        phoneVerified: ownerProfile.phoneVerified || false,
        status: ownerProfile.status || "PENDING",
        onboardingStep: ownerStep,
      };
      setAuthData(merged);
      return merged;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update the stored onboarding step (called by Onboarding page as user progresses).
   */
  const updateOnboardingStep = useCallback((step) => {
    setAuthData((prev) => prev ? { ...prev, onboardingStep: step } : prev);
  }, []);

  /**
   * Update auth data fields after verification (e.g. isVerified, phoneVerified).
   */
  const updateAuthProfile = useCallback((updates) => {
    setAuthData((prev) => prev ? { ...prev, ...updates } : prev);
  }, []);

  const logout = () => {
    setAuthData(null);
  };

  const getAccessToken = () => authData?.accessToken || null;

  const getAuthHeader = () => {
    const t = getAccessToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        googleLogin,
        updateOnboardingStep,
        updateAuthProfile,
        getAccessToken,
        getAuthHeader,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
